import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuButton from "../MenuButton";
import ActionButton from "../ActionButton";
import SearchResults from "../SearchResults";
import VersionBadge from "../../ui/VersionBadge";
import { useFileActions } from "../../../contexts/FileActionsContext";

function Header({ handleSignOut }) {
  const [searchQuery, setSearchQuery] = useState("");
  const {
    searchResults,
    loadingSearch,
    searchFilesAndFolders,
    setCurrentFolder,
  } = useFileActions();
  const { tenant } = useParams();
  const navigate = useNavigate();

  const handleResultClick = (result) => {
    if (result.type === "folder") {
      setCurrentFolder(result.fullPath);
      setSearchQuery("");
    } else {
      // For files, navigate to the file's folder and then open the file via URL param
      setCurrentFolder(result.folder || ""); // Navigate to the file's folder
      const searchParams = new URLSearchParams();
      searchParams.set("file", result.fullPath);
      // Use replace to avoid adding multiple entries to history when navigating to the same folder
      navigate(`/${tenant}?path=${result.folder || ""}&${searchParams.toString()}`);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchFilesAndFolders(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, searchFilesAndFolders]);

  return (
    <AppBar
      position="relative"
      sx={{
        backgroundColor: "#ffffff",
        boxShadow: "none",
        borderBottom: "1px solid #e0e0e0",
        overflow: "visible",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", px: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <img
            src="/logo192.png"
            alt="Drive Logo"
            style={{
              width: "28px",
              height: "28px",
              marginRight: "8px",
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: "#333333",
              fontWeight: 700,
              fontSize: { xs: "1.15rem", sm: "1.25rem" },
              letterSpacing: "-0.3px",
            }}
          >
            Drive
          </Typography>
          <VersionBadge sx={{ ml: 1 }} />
        </Box>

        {/* Search bar for larger screens (inside Toolbar) */}
        <Box sx={{
          flexGrow: 1,
          display: { xs: 'none', sm: 'flex' }, // Hidden on xs, visible on sm and up
          justifyContent: 'center',
          position: 'relative', // For SearchResults absolute positioning
          mx: 2, // Horizontal margin
        }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              "data-testid": "search-input-large-screen",
            }}
            sx={{ width: "100%", maxWidth: "500px" }}
          />
          {searchQuery && (
            <SearchResults
              results={searchResults}
              loading={loadingSearch}
              onResultClick={handleResultClick}
              sx={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                backgroundColor: 'background.paper',
                boxShadow: 3,
                maxHeight: '300px',
                overflowY: 'auto',
                width: '100%',
                maxWidth: '500px',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ActionButton />
          <MenuButton handleSignOut={handleSignOut} />
        </Box>
      </Toolbar>

      {/* Search bar for smaller screens (below Toolbar) */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' }, // Visible on xs, hidden on sm and up
          justifyContent: 'center',
          px: 2,
          py: 1,
          // No absolute positioning here, let it flow normally
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: "100%" }}
          />
          {searchQuery && (
            <SearchResults
              results={searchResults}
              loading={loadingSearch}
              onResultClick={handleResultClick}
              sx={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                backgroundColor: 'background.paper',
                boxShadow: 3,
                maxHeight: '300px',
                overflowY: 'auto',
                width: '100%',
                maxWidth: '500px',
              }}
            />
          )}
        </Box>
      </Box>
    </AppBar>
  );
}

export default Header;
